/* eslint-disable react-hooks/exhaustive-deps */
import { TypeDefinitionTypes } from '@eagle/common';
import { FeatureTypes } from '@eagle/data-function-types';
import { Chip, styled } from '@ethings/ui-toolkit';
import { SxProps } from '@mui/system';
import { FC, forwardRef, useEffect, useRef, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { ALERT_LIST_ALERT_TYPE_FILTER_V2_FLAG, getEventLabelWithFlag, getFeatureLabelWithFlag, getFeatureWithInstanceLabel } from '../../util';
import { testid } from '../../util/test-id';
import { AppliedFilter, EntityField } from '../entity-search/types';
import { useBoolFlag } from '../flags';

interface Props {
  disabled?: boolean;
  filter: AppliedFilter;
  onRemoveClicked: () => unknown;
  sx?: SxProps;
}

interface StyledChipProps extends ComponentProps<typeof Chip> {
  sx?: SxProps;
}

const BaseStyledChip = styled(Chip)<StyledChipProps>(({ theme }) => ({
  margin: '0 0.5rem 0.7rem 0',
  '& .MuiChip-deleteIcon': {
    color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600],
  },
}));

const StyledChip = forwardRef<HTMLDivElement, StyledChipProps>(({ sx, ...rest }, ref) => (
  <BaseStyledChip ref={ref} sx={sx} {...rest} />
));

StyledChip.displayName = 'StyledChip';

export const FilterElement: FC<Props> = ({ disabled, filter, onRemoveClicked, sx }) => {
  const { t } = useTranslation(['common']);
  const chipRef = useRef(null);
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const isAlertTypeFilterV2Enabled = useBoolFlag(ALERT_LIST_ALERT_TYPE_FILTER_V2_FLAG);

  const formatValue = (): string => {
    if ((filter.value as EntityField).alertProperties) {
      const properties = (filter.value as EntityField).alertProperties;
      const featureDisplay = isAlertTypeFilterV2Enabled ?
        getFeatureWithInstanceLabel(properties?.feature as string) :
        getFeatureLabelWithFlag(properties?.feature as string, flagUseEventV3);
      const alertDisplay = getEventLabelWithFlag(properties?.featureType as string, properties?.alert as string, properties?.feature as string, flagUseEventV3);
      return properties?.featureType === FeatureTypes.EVENT_RECORD_V0 ? `${featureDisplay} - ${alertDisplay}` : `${alertDisplay} - ${featureDisplay}`;
    }
    if (filter.definition.type === 'entity' || filter.definition.type === TypeDefinitionTypes.REFERENCE) {
      return t('common:component.filter.labels.is', {
        field: filter.definition.label,
        value: (filter.value as EntityField).display,
      });
    }
    return t('common:component.filter.labels.is', {
      field: filter.definition.label,
      value: filter.value as string,
    });
  };

  const formatTestId = (): string => {
    let value: string;
    if (filter.definition.type === 'entity' || filter.definition.type === TypeDefinitionTypes.REFERENCE) {
      value = (filter.value as EntityField).display;
    } else {
      value = filter.value.toString();
    }
    return (testid`filter-${filter.definition.label}-${value}`);
  };

  const keyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === ' ' && document.activeElement === chipRef.current) {
      onRemoveClicked();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [keyDownHandler]);

  return (
    <StyledChip
      data-testid={formatTestId()}
      ref={chipRef}
      disabled={disabled}
      label={formatValue()}
      onDelete={onRemoveClicked}
      sx={sx}
    />
  );
};
